import React, { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'


import {
  Button,
  Container,
  Dropdown,
  FAQ,
  GridContainer,
  GridColumn,
  Heading,
  IconButton,
  IconLink,
  Legend,
  Main,
  NavBar,
  ProgressionIndicator,
  Text,
  UseIcon,
  Loader, CompanyLogo
} from '@domain/components';
import {
  getPrevNextPagePath,
  goToNextPage,
  browserStorage,
  generatePageContent,
  chooseLogoSize,
} from '@domain/helpers';
import { useNavigate, useLocation } from 'react-router-dom'
import { useFaq, useScaling, useEnterKey, useSaveIncidentFactory } from '@domain/hooks';
import { styled, useTheme } from '@mui/material/styles';
import { detectContentType } from '@domain/content';
import { isMobile } from 'react-device-detect';
import { useQuestionnaire } from '@domain/providers';
import { causeDamageCategoriesCombinedDropdown } from '@domain/constants/causeDamageCategories';


const faqs = [
  {
    question: 'Waarom moet ik opgeven hoe de schade is ontstaan?',
    answer: 'Dit is informatie die we met uw verzekeraar moeten delen en is daarom altijd onderdeel van een schadedossier. De verzekeraar deelt schades standaard in in verschillende groepen.'
  },
  {
    question: 'Wat betekenen de verschillende oorzaken precies?',
    answer: (
      <dl>
        <dt>Aanrijding met voertuig - </dt>
        <dd>
          U heeft een aanrijding gehad met een ander gemotoriseerd voertuig, dit
          kan bijvoorbeeld een auto, busje, vrachtwagen of scooter zijn.
        </dd>
        <br />
        <br />
        <dt>Aanrijding met voetganger/fiets - </dt>
        <dd>
          U heeft een aanrijding gehad met een niet-gemotoriseerd voertuig zoals
          een fiets of een voetganger.
        </dd>
        <br />
        <br />
        <dt>Aanrijding met dier - </dt>
        <dd>
          U heeft een aanrijding gehad met een dier op de weg, bijvoorbeeld een
          hond, hert of gans.
        </dd>
        <br />
        <br />
        <dt>Aanrijding met (vast) object - </dt>
        <dd>
          U heeft bijvoorbeeld een aanrijding gehad met een hekje, een vangrail
          of een bord langs de weg.
        </dd>{' '}
        <br />
        <br />
        <dt>Inbraak - </dt>
        <dd>
          Er is in uw auto ingebroken en er zijn eigendommen van u uit de auto
          gestolen of er zijn onderdelen van uw auto uit uw auto gestolen.
        </dd>{' '}
        <br />
        <br />
        <dt>Vandalisme - </dt>
        <dd>Uw auto is (opzettelijk) beschadigd aan binnen- of buitenkant.</dd>
        <br />
        <br />
        <dt>Hagel/storm - </dt>
        <dd>
          Uw auto heeft schade opgelopen als gevolg van een hagelbui of een
          storm.
        </dd>
        <br />
        <br />
        <dt>Brand - </dt>
        <dd>
          Uw auto heeft schade opgelopen als gevolg van een brand in of in de
          buurt van uw auto.
        </dd>
      </dl>
    ),
  },
];

const StyledMain = styled(Main, {
  shouldForwardProp: (propName) => !propName.startsWith('$')
})`
  .bodyshop-select {
    &__container {
      justify-content: center;
      .grid__column {
        flex: 0 0 auto;
        &.bodyshop-select__main {
          padding-top: 30px;
        }
      }
    }
    &__title {
      flex: 1;
      h1 {
        margin-bottom: 0;
      }
    }
    &__main {
      padding: 20px 0;
      // flex-direction: row;
      }
    }
    &__footer {
      flex-direction: row;
      justify-content: flex-end;
      &--button-container {
        width: 100%;
        .button {
          width: 100%;
        }
      }
    }
  }

  .screen-desktop & {
    .bodyshop-select {
      &__container {
        //padding: 0 ${props => props.$scaling.scaleUpTo4K(120)}px ${props => props.$scaling.scaleUpTo4K(82)}px;
        flex-direction: column;
      }
      &__title {
        padding: ${props => props.$scaling.scaleUpTo4K(60)}px 0 ${props => props.$scaling.scaleUpTo4K(30)}px;
      }
      &__main {
        padding: 0;
        flex-direction: column;
      }
      &__footer {
        // padding-bottom: ${props => props.$scaling.scaleUpTo4K(60)}px;
        &--button-container {
          width: auto;
          .button {
            width: ${props => props.$scaling.scaleUpTo4K(240)}px;
          }
        }
      }
    }
    .grid__column--main {
      max-width: none;
    }
  }

  @media screen and (max-width: 1028px) {
    .screen-desktop & {
      .bodyshop-select {
        &__container {
          .grid__column {
            flex: 0 0 auto;
            padding-top: 0;
          }
        }
        &__title {
          text-align: center;
          h1 {
            max-width: none;
          }
        }
        &__main {
        }
        &__footer {
          max-width: 500px;
          margin: auto auto 0;
          padding-bottom: 0;
          &--button-container {
            margin: auto;
          }
        }
      }
    }
  }

  @media screen and (max-width: 580px) {
    .bodyshop-select {
      &__container {
        .grid__column {
          &.bodyshop-select__main {
            flex: 1 0 auto;
          }
        }
      }
      &__title {
        h1 {
        }
      }
      &__main {
        .list__container {
        }
      }
      &__footer {
      }
    }

    .screen-desktop & {
      .bodyshop-select {
        &__container {
          .grid__column {
          }
        }
        &__title {
          text-align: left;
          h1 {
            margin-left: 0;
            flex-grow: 0;
          }
        }
        &__main {
          .list__container {
          }
        }
        &__footer {
          &--button-container {
            width: 100%;
            .button {
              width: 100%;
            }
          }
        }
      }
    }
  }
`;

function CauseDamageCombinedDropdown() {

  const navigate = useNavigate()
  const { questionnaire } = useQuestionnaire();
  const incident = useSelector(state => state.incident);
  const insurer = useSelector(state => state.insurer);
  const files = useSelector(state => state.files)

  const scaling = useScaling();

  const themeContext = useTheme();

  const [
    faqVisible,
    handleOnHelpClick,
    clickOutSideFaq,
    faqListProps,
    faqSeen,
  ] = useFaq();

  const currentValue = causeDamageCategoriesCombinedDropdown.includes(incident.causeDamage) ? incident.causeDamage : "";
  const [selectedValue, setSelectedValue] = useState(currentValue || "");
  const [buttonClicked, setButtonClicked] = useState(false)

  const location = useLocation()
  const currentPath = location.pathname;
  const page = questionnaire.find(page => page.path === currentPath)
  const saveBehavior = page ? page.saveBehavior : undefined

  const saveIncident = useSaveIncidentFactory(incident, saveBehavior);

  useEffect(() => {
    if (!!questionnaire && questionnaire.length) {
      const progress = questionnaire.find(page => page.path === currentPath)
      browserStorage.setProgress(progress.name);
      browserStorage.setProgressIndex(progress.index);
    }
  }, [currentPath, questionnaire])

  const isPageBeforeSuccess = questionnaire[questionnaire.length - 2].path === currentPath

  async function handleClick(event) {
    event.preventDefault();
    const savedIncident = await saveIncident({ ...incident, causeDamage: selectedValue }, isPageBeforeSuccess)
    if (savedIncident) {
      setButtonClicked(true)
    }
  }

  useEffect(() => {
    if (buttonClicked) {
      if (incident.causeDamage === selectedValue) {
        goToNextPage(navigate, questionnaire, incident, currentPath, files);
      }
    }
  }, [buttonClicked, files, selectedValue, incident, questionnaire, currentPath,]);

  const [errors, setErrors] = useState(!currentValue || !selectedValue);

  const nextPageEnabled = !errors

  useEnterKey(handleClick, [errors]);


  if (!insurer || !incident || !questionnaire || (questionnaire && !questionnaire.length)) {
    return <Loader />
  }

  const handleClickItem = (event) => {
    setSelectedValue(event.target.value);
    setErrors(false)
  }

  const pagePath = getPrevNextPagePath(
    questionnaire,
    incident,
    currentPath,
    files,
  );
  const nextPage = pagePath.nextPage !== currentPath
    ? pagePath.nextPage
    : undefined;

  const main = {
    faqVisible: faqVisible,
    dimmed: faqVisible,
    className: 'main__damage main__damage--bodyshop-select',
    callBack: clickOutSideFaq,
    $scaling: scaling,
  }

  const iconLink = (triggerKey) => ({
    type: 'arrow-left',
    theme: 'secondary',
    to: pagePath.prevPage,
    replace: true,
    triggerKey
  });

  const iconLinkForward = (triggerKey) => ({
    type: 'arrow-right',
    theme: 'secondary',
    to: nextPage,
    replace: true,
    triggerKey
  });

  const iconButton = {
    type: faqVisible ? 'arrow-right' : 'questionmark',
    className: faqSeen ? '' : 'unclicked-faq-button',
    theme: 'secondary',
    onClick: handleOnHelpClick,
    faq: true,
  };

  const button = {
    className: 'button--icon',
    justify: 'space-between',
    onClick: handleClick,
    shadow: true,
    disabled: errors,
    theme: nextPageEnabled ? 'primary' : 'disabled',
  };

  const contentType = detectContentType(insurer)
  const pageName = questionnaire.find(page => page.path === currentPath).name
  const content = generatePageContent(contentType, insurer, incident, false, false, pageName)

  const legend = content["over-title-text"]
  const heading = content["title"]
  const customHelpLink = content["custom-help-link"]
  const helpLinkText = content["help-link-text"]
  const useCustomLogo = content["use-custom-logo"]
  const buttonText = content["button-text"]

  const middleLogo = {
    src: insurer.logoURL || '',
    alt: insurer.brandName || 'uw verzekeraar',
    width: '100%',
    height: chooseLogoSize(themeContext.logoSize),
  }

  const chooseMiddleComponent = useCustomLogo
    ? <CompanyLogo {...middleLogo} />
    : <ProgressionIndicator steps={3} current={2} />

  return (
    <React.Fragment>
      <FAQ
        faqListProps={faqListProps}
        faqs={faqs}
        faqLink={insurer.helpcenterLink}
        customHelpLink={customHelpLink}
        helpLinkText={helpLinkText}
        faqVisible={faqVisible}
      />
      <StyledMain {...main}>
        <NavBar>
          <Container className="icon-link-container">
            <IconLink {...iconLink([37])} />
            {nextPage && <IconLink {...iconLinkForward([39])} />}
          </Container>
          {chooseMiddleComponent}
          <Container className="faq-button-container">
            <IconButton {...iconButton} size="large" />
          </Container>
        </NavBar>
        <GridContainer main={true}>
          <GridColumn className="grid__column--main">
            <Legend>{legend}</Legend>
            <Heading level="1" className={'dropdown'}>{heading}</Heading>
            <Dropdown
              id="dropdownMerk"
              label="Schadeoorzaken"
              onChange={handleClickItem}
              value={selectedValue}
              valueList={causeDamageCategoriesCombinedDropdown}
            />
          </GridColumn>
          <GridColumn
            justify="flex-end"
            className="grid__column--footer"
          >

            <Button {...button}>
              <span>{buttonText || ' Correct'}</span>
              <UseIcon name="arrow-right" className="button__icon" />
            </Button>
            {!isMobile && (
              <Text className="of--enter">
                of <b>ENTER</b>
              </Text>
            )}
          </GridColumn>
        </GridContainer>
      </StyledMain>
    </React.Fragment>
  );
}

export default CauseDamageCombinedDropdown;
